package se.mittpass.shared.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.client.statement.bodyAsText
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import se.mittpass.shared.auth.AuthStorage
import se.mittpass.shared.config.apiBaseUrl

/**
 * Creates the shared HTTP client.
 *
 * @param authStorage token store used for Bearer auth and refresh-token rotation
 * @param onUnauthorized called when a 401 cannot be recovered by token refresh
 *                       (e.g. redirect to login screen)
 * @param onPaymentRequired called when a 402 is returned (subscription expired/payment due)
 */
fun createHttpClient(
    authStorage: AuthStorage,
    onUnauthorized: (() -> Unit)? = null,
    onPaymentRequired: ((String) -> Unit)? = null
): HttpClient {
    val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
        defaultRequest {
            url(apiBaseUrl())
            contentType(ContentType.Application.Json)
            authStorage.getToken()?.let { token ->
                header(HttpHeaders.Authorization, "Bearer $token")
            }
        }
    }

    // 401/402 → refresh/callback.
    // Must be installed on the built client, not inside HttpClientConfig.
    client.plugin(HttpSend).intercept { request ->
        // Snapshot whether this was an authenticated request before executing.
        // Only requests that carried a Bearer token can legitimately trigger session expiry —
        // a 401 from e.g. /auth/login (wrong password, no token sent) must not show the banner.
        val wasAuthenticated = request.headers.contains(HttpHeaders.Authorization)

        val originalCall = execute(request)
        
        if (originalCall.response.status == HttpStatusCode.PaymentRequired) {
            val errorMsg = try {
                val bodyText = originalCall.response.bodyAsText()
                val json = Json.parseToJsonElement(bodyText)
                json.jsonObject["error"]?.jsonPrimitive?.content
            } catch (_: Exception) {
                null
            } ?: "Provperioden har gått ut. Gå till Fakturering för att aktivera ditt konto."
            onPaymentRequired?.invoke(errorMsg)
            return@intercept originalCall
        }

        if (originalCall.response.status != HttpStatusCode.Unauthorized) {
            return@intercept originalCall
        }

        val refreshToken = authStorage.getRefreshToken() ?: run {
            if (wasAuthenticated) onUnauthorized?.invoke()
            return@intercept originalCall
        }

        // Attempt token refresh — fresh builder avoids mutating the original request
        val refreshRequest = HttpRequestBuilder().apply {
            url("${apiBaseUrl()}auth/refresh")
            method = HttpMethod.Post
            contentType(ContentType.Application.Json)
            setBody(Json.encodeToString(RefreshTokenRequest.serializer(), RefreshTokenRequest(refreshToken)))
        }
        val refreshCall = execute(refreshRequest)
        if (!refreshCall.response.status.isSuccess()) {
            authStorage.clear()
            onUnauthorized?.invoke()
            return@intercept originalCall
        }

        val newAuth = refreshCall.response.body<AuthResponse>()
        authStorage.setToken(newAuth.token)
        newAuth.refreshToken?.let { authStorage.setRefreshToken(it) }

        // Retry original request with the new access token
        request.headers.remove(HttpHeaders.Authorization)
        request.headers.append(HttpHeaders.Authorization, "Bearer ${newAuth.token}")
        execute(request)
    }

    return client
}
